import { type Adjustment, type CommitmentManagersModel, type Period, type PeriodSpend } from "@doitintl/cmp-models";
import { DateTime } from "luxon";
import * as Yup from "yup";

import { type MetadataOption } from "../../types";
import { assetTypeName } from "../../utils/common";
import { type TableData, type TableRow } from "./types";

const step0 = Yup.object({
  contractType: Yup.string().required("Provider selection is required"),
  name: Yup.string().required("Commitment name is required"),
  selectedContractIds: Yup.array(),
  currency: Yup.string().required("Currency is required"),
});
const step1 = Yup.object({
  startDate: Yup.string().required("Start date is required"),
  periods: Yup.array().of(
    Yup.object().shape({
      periodLength: Yup.number().max(100, "Value must be under 100").required("Period length is required"),
      commitmentValue: Yup.number().typeError("Must be a number").required("Commitment value is required"),
      marketplaceLimitPercentage: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be between 0 and 100")
        .max(100, "Must be between 0 and 100")
        .required("Marketplace spend value is required"),
    })
  ),
  excess: Yup.boolean(),
  excessValue: Yup.number()
    .typeError("Must be a number")
    .when("excess", {
      is: true,
      then: (schema) =>
        schema
          .min(1, "Value must be at least 1")
          .max(100, "Value must be under 100")
          .required("Excess value is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  shortfall: Yup.boolean(),
  shortfallValue: Yup.number()
    .typeError("Must be a number")
    .when("shortfall", {
      is: true,
      then: (schema) =>
        schema
          .min(1, "Value must be at least 1")
          .max(100, "Value must be under 100")
          .required("Shortfall value is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
});
const step2 = Yup.object({
  billingAccounts: Yup.array()
    .required("Billing accounts are required")
    .min(1, "At least one billing account must be selected"),
});
export const validationSchema = [step0, step1, step2];

export const formatDate = (date: Date) => DateTime.fromJSDate(date, { zone: "utc" }).toFormat("dd LLL yyyy");

export const addMonths = (date: Date, months: number) =>
  DateTime.fromJSDate(date, { zone: "utc" }).plus({ months }).toJSDate();

export const getFieldName = (selected: MetadataOption) => {
  const label = selected.data.label ?? selected.data.field;
  const plural = selected.data.plural ?? label;
  return plural;
};

export function getDaysBetween(startDate: Date, endDate: Date): number {
  const start = DateTime.fromJSDate(startDate, { zone: "utc" });
  const end = DateTime.fromJSDate(endDate, { zone: "utc" });
  const diff = end.diff(start, "days").days;
  return Math.max(0, diff);
}

export function processBreakdown(
  data: Record<string, Record<string, number>>,
  source: Record<string, number> = {},
  month: string
): void {
  Object.entries(source).forEach(([key, value]) => {
    data[key] = data[key] || {};
    data[key][month] = value;
  });
}

export function calcTableData(commitmentManager: CommitmentManagersModel | undefined | null): TableData[] {
  if (!commitmentManager?.periodsSpend) return [];

  return commitmentManager.periodsSpend.map((periodSpend) => {
    const monthlySpend = periodSpend.MonthlySpend;
    const months = Object.keys(monthlySpend).sort();

    const servicesData: Record<string, Record<string, number>> = {}; // <name, <month, spend>>
    const creditsData: Record<string, Record<string, number>> = {};
    const marketplaceData: Record<string, number> = {}; // <month, spend>
    const marketplaceEligableData: Record<string, number> = {}; // <month, eligable>
    const totalsData: Record<string, number> = {};

    let previousEligableMarketplace = 0;
    months.forEach((month) => {
      const breakdown = monthlySpend[month];

      marketplaceData[month] = breakdown?.marketplaceTotal ?? 0;
      marketplaceEligableData[month] = previousEligableMarketplace;
      previousEligableMarketplace += breakdown?.marketplaceTotal ?? 0;

      totalsData[month] = breakdown?.total ?? 0;

      processBreakdown(servicesData, breakdown?.services, month);
      processBreakdown(creditsData, breakdown?.credits, month);
    });

    const rows = [...createCategoryRows(servicesData, "service"), ...createCategoryRows(creditsData, "credit")];

    const totalRow = {
      name: "Total",
      type: "total",
      data: totalsData,
      total: Object.values(totalsData).reduce((sum, val) => sum + val, 0),
    };

    const marketplaceRow = {
      name: "Marketplace",
      type: "marketplace",
      data: marketplaceData,
      total: Object.values(marketplaceData).reduce((sum, val) => sum + val, 0),
    };

    if (marketplaceRow.total > 0) {
      rows.push(marketplaceRow);
    }

    return {
      months,
      rows,
      totalRow,
      marketplaceEligableData,
    };
  });
}

function createCategoryRows(categoryData: Record<string, Record<string, number>>, type: string): TableRow[] {
  return Object.keys(categoryData).map((name) => ({
    name,
    type,
    data: categoryData[name],
    total: Object.values(categoryData[name]).reduce((sum, val) => sum + val, 0),
  }));
}

export function getContractName(type: string, startDate: Date, endDate?: Date) {
  return `${assetTypeName(type)} - ${formatDate(startDate)} ${endDate ? `-> ${formatDate(endDate)}` : ""}`;
}

export const getPeriodStatus = (startDate: Date, endDate: Date): "ongoing" | "inactive" | "notStarted" => {
  const today = DateTime.utc();
  const start = DateTime.fromJSDate(startDate, { zone: "utc" });
  const end = DateTime.fromJSDate(endDate, { zone: "utc" });

  if (today < start) {
    return "notStarted";
  } else if (today > end) {
    return "inactive";
  } else {
    return "ongoing";
  }
};

export const getCurrentPeriodIndex = (periods?: Period[], currentDate: Date = new Date()): number => {
  if (!periods?.length) {
    return -1;
  }

  const current = DateTime.fromJSDate(currentDate, { zone: "utc" });
  const currentPeriodIndex = periods.findIndex(
    (period) =>
      current >= DateTime.fromJSDate(period.startDate.toDate(), { zone: "utc" }) &&
      current <= DateTime.fromJSDate(period.endDate.toDate(), { zone: "utc" })
  );

  // If no current period found, return the last period
  return currentPeriodIndex >= 0 ? currentPeriodIndex : periods.length - 1;
};

export const calucluateTotalPeriodSpend = (periodSpend?: PeriodSpend, adjustments?: Adjustment[]) => {
  if (!periodSpend) {
    return 0;
  }

  const totalAdjustments = adjustments?.reduce((sum: number, adj: Adjustment) => sum + Number(adj.amount), 0) ?? 0;
  const total = Number(periodSpend.total) || 0; // total includes rollover from previous period
  const shortfallRollover = Number(periodSpend.shortfallRollover) || 0;

  return total + shortfallRollover + totalAdjustments;
};

export const calculateRemaining = (periodSpend?: PeriodSpend, period?: Period, adjustments?: Adjustment[]) => {
  if (!periodSpend || !period) {
    return period?.commitmentValue ?? 0;
  }

  return period.commitmentValue - calucluateTotalPeriodSpend(periodSpend, adjustments);
};
